import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiTrendingUp, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import API from '../../utils/api';
import './AdminSearchAnalytics.css';

const PERIODS = [
  { value: '7d',  label: 'Last 7 days'  },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

const AdminSearchAnalytics = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [period,  setPeriod]  = useState('30d');
  const [type,    setType]    = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    API.get(`/catalog/search/analytics?period=${period}&type=${type}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period, type]);

  useEffect(() => { load(); }, [load]);

  const zeroRate = data?.summary?.zeroResultRate ?? '0.0';
  const zeroColor = parseFloat(zeroRate) > 30 ? '#dc2626' : parseFloat(zeroRate) > 15 ? '#d97706' : '#16a34a';

  return (
    <div className="sa-page">
      <div className="sa-header">
        <div>
          <h1 className="sa-title"><FiSearch size={20} /> Search Analytics</h1>
          <p className="sa-subtitle">See what customers are searching for and where the gaps are.</p>
        </div>
        <button className="sa-refresh" onClick={load} disabled={loading}>
          <FiRefreshCw size={15} className={loading ? 'sa-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Period + Type Filters */}
      <div className="sa-filters">
        <div className="sa-filter-group">
          {PERIODS.map(p => (
            <button
              key={p.value}
              className={`sa-filter-btn ${period === p.value ? 'active' : ''}`}
              onClick={() => setPeriod(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="sa-filter-group">
          <button className={`sa-filter-btn ${type === 'all'  ? 'active' : ''}`} onClick={() => setType('all')}>All searches</button>
          <button className={`sa-filter-btn ${type === 'zero' ? 'active' : ''}`} onClick={() => setType('zero')}>Zero-result only</button>
        </div>
      </div>

      {loading && !data && (
        <div className="sa-loading">Loading analytics…</div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="sa-summary">
            <div className="sa-card">
              <div className="sa-card-icon" style={{ background: 'rgba(0,120,191,0.1)', color: 'var(--primary)' }}>
                <FiSearch size={20} />
              </div>
              <div>
                <div className="sa-card-value">{data.summary.totalSearches.toLocaleString()}</div>
                <div className="sa-card-label">Total searches</div>
              </div>
            </div>
            <div className="sa-card">
              <div className="sa-card-icon" style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626' }}>
                <FiAlertCircle size={20} />
              </div>
              <div>
                <div className="sa-card-value">{data.summary.zeroResultSearches.toLocaleString()}</div>
                <div className="sa-card-label">Zero-result searches</div>
              </div>
            </div>
            <div className="sa-card">
              <div className="sa-card-icon" style={{ background: 'rgba(16,163,74,0.1)', color: zeroColor }}>
                <FiTrendingUp size={20} />
              </div>
              <div>
                <div className="sa-card-value" style={{ color: zeroColor }}>{zeroRate}%</div>
                <div className="sa-card-label">Zero-result rate</div>
              </div>
            </div>
          </div>

          {/* Daily Volume Sparkline */}
          {data.dailyVolume?.length > 0 && (
            <div className="sa-section">
              <h2 className="sa-section-title">Daily Search Volume</h2>
              <div className="sa-volume-grid">
                {data.dailyVolume.map(d => {
                  const max = Math.max(...data.dailyVolume.map(x => parseInt(x.total) || 0), 1);
                  const pct = ((parseInt(d.total) || 0) / max) * 100;
                  const zeroPct = parseInt(d.total) > 0
                    ? ((parseInt(d.zeroResults) || 0) / parseInt(d.total)) * 100
                    : 0;
                  return (
                    <div key={d.date} className="sa-bar-col" title={`${d.date}: ${d.total} searches, ${d.zeroResults} zero-result`}>
                      <div className="sa-bar-wrap">
                        <div className="sa-bar-total" style={{ height: `${pct}%` }}>
                          <div className="sa-bar-zero" style={{ height: `${zeroPct}%` }} />
                        </div>
                      </div>
                      <div className="sa-bar-label">{d.date?.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
              <div className="sa-legend">
                <span className="sa-legend-dot" style={{ background: 'var(--primary)' }} /> Total
                <span className="sa-legend-dot" style={{ background: '#dc2626' }} /> Zero results
              </div>
            </div>
          )}

          {/* Top Search Terms Table */}
          <div className="sa-section">
            <h2 className="sa-section-title">
              {type === 'zero' ? '⚠️ Zero-Result Searches (Fix These)' : 'Top Search Terms'}
            </h2>
            {data.topTerms?.length === 0 ? (
              <p className="sa-empty">No search data for this period.</p>
            ) : (
              <div className="sa-table-wrap">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Search Term</th>
                      <th>Times Searched</th>
                      <th>Avg Results</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topTerms.map((row, i) => {
                      const avg = parseFloat(row.avgResults) || 0;
                      const isZero = avg < 1;
                      return (
                        <tr key={row.query} className={isZero ? 'sa-row-zero' : ''}>
                          <td className="sa-rank">{i + 1}</td>
                          <td className="sa-query">
                            <FiSearch size={13} />
                            {row.query}
                          </td>
                          <td className="sa-count">
                            <span className="sa-count-bar" style={{ width: `${Math.min(100, (row.searchCount / data.topTerms[0]?.searchCount) * 100)}%` }} />
                            {parseInt(row.searchCount).toLocaleString()}
                          </td>
                          <td>{avg.toFixed(1)}</td>
                          <td>
                            {isZero
                              ? <span className="sa-badge sa-badge-zero">No results</span>
                              : <span className="sa-badge sa-badge-ok">Has results</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {type === 'zero' && data.topTerms?.length > 0 && (
              <div className="sa-tip">
                <FiAlertCircle size={15} />
                These are searches where customers found nothing. For each term, consider:
                adding it as a product tag, renaming a product, or creating a new product if there's clear demand.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminSearchAnalytics;
